/**
 * useMemberCalendlyCall
 *
 * Fetches all Calendly call logs for a member.
 * Use the `select` option to filter/transform the data for specific use cases.
 *
 * @param queryKeyPrefix - Unique prefix for the React Query cache key
 * @param options - Query options including `select` for data transformation
 * @returns Query result with Calendly log data
 */
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import fetchMemberCalendlyLogs from './useMemberCalendlyCall.request';
import { CalendlyLogResponse } from './useMemberCalendlyCall.interface';

import { useMember } from 'modules/common/queries/member';

export type { CalendlyLogResponse } from './useMemberCalendlyCall.interface';

type UseMemberCalendlyCallOptions<TData> = Omit<
    UseQueryOptions<CalendlyLogResponse[], Error, TData>,
    'queryKey' | 'queryFn'
>;

export default function useMemberCalendlyCall<TData = CalendlyLogResponse[]>(
    queryKeyPrefix: string,
    options?: UseMemberCalendlyCallOptions<TData>
) {
    const { data: member } = useMember();

    // Combine base enabled condition with any additional enabled from options
    const baseEnabled = !!member?.email;
    const optionsEnabled = options?.enabled ?? true;

    return useQuery({
        queryKey: [queryKeyPrefix, member?.email],
        queryFn: fetchMemberCalendlyLogs,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: 'always',
        ...options,
        enabled: baseEnabled && optionsEnabled,
    });
}
